import { getCountrySpecificRules } from '@helpers/config';
import { intl } from '@helpers/intl';

import { formatRegDate, parseRegDate } from '../helpers';

/**
 * @typedef {Object} RegistrationApiData
 * @property {string} id Registration ID
 * @property {Object} entity Parent entity data
 * @property {string} entity.id Entity ID
 * @property {string} entity.name Entity name
 * @property {Object} country Entity country information
 * @property {string} country.id Country ID
 * @property {string} country.name Country name
 * @property {string} country.iso Country ISO code
 * @property {string} number VAT number
 * @property {string} numberLocal Local VAT number
 * @property {string} dateRegistration Registration date
 * @property {string} dateRegistrationClosed De-registration date
 * @property {string} dateFilingStart End date for period of first filing
 * @property {string} fiscalCode Fiscale code
 * @property {string} status Registration status
 * @property {string} registrationSubType Registration type
 * @property {string} registrationSubTypeDisplay Registration type display name
 * @property {boolean} isNotManagedByUs Whether account is managed by us
 */

/**
 * @typedef {Object} RegistrationDeleteOrDeactivateApiData
 * @property {Object} canBeDeletedOrDeactivated
 * @property {boolean} canBeDeletedOrDeactivated.canDeactivate Allow de-activation
 * @property {boolean} canBeDeletedOrDeactivated.canDelete Allow deletion
 */

/**
 * @typedef {Object} RegistrationModel
 * @property {string} id Registration ID
 * @property {string} entityId Entity ID
 * @property {string} entityName Entity name
 * @property {string} entityJurisdictionId Entity Jurisdiction ID
 * @property {string} jurisdictionName Jurisdiction name
 * @property {string} iso Country ISO code
 * @property {string} vatNumber VAT number
 * @property {string} [vatNumberLocal] Local VAT number
 * @property {Date} dateRegistration Registration date
 * @property {string} dateRegistrationDisplay Registration date in display format
 * @property {Date} dateRegistrationClosed De-registration date
 * @property {string} dateRegistrationDisplayClosed De-registration date in display format
 * @property {Date} dateFilingStart End date for period of first filing
 * @property {string} dateFilingStartDisplay End date for period of first filing in display format
 * @property {string} [fiscaleCode] Fiscale code
 * @property {string} status Registration status
 * @property {boolean} canDelete Allow deletion
 * @property {boolean} canDeactivate Allow de-registration
 * @property {string} registrationType Registration sub type
 * @property {string} registrationTypeLabel Registration type display name
 */

/**
 * Model to transform incoming registration API data
 * @param {RegistrationApiData} [data={}] Registration data
 * @returns {RegistrationModel} Formatted registration data
 */
export const registrationsDetailGetModel = (data = {}) => {
  const iso = data.country?.iso;
  const rules = getCountrySpecificRules(iso, 'registrations');
  const notAvailable = intl.formatMessage({
    id: 'global.value.na',
    defaultMessage: 'N/A',
  });

  const model = {
    id: data.id,
    vatNumber: data.number || '',
    dateRegistration: parseRegDate(data.dateRegistration),
    dateRegistrationDisplay: formatRegDate(data.dateRegistration),
    dateRegistrationClosed: parseRegDate(data.dateRegistrationClosed),
    dateRegistrationClosedDisplay: formatRegDate(data.dateRegistrationClosed),
    dateFilingStart: parseRegDate(data.dateFilingStart),
    dateFilingStartDisplay: formatRegDate(data.dateFilingStart),
    taxableJurisdictionId: data.taxableJurisdiction || '',
    jurisdictionName: data.country?.name || '',
    iso,
    entityId: data.entity?.id,
    entityName: data.entity?.name || '',
    registrationType: data.registrationSubType,
    registrationTypeLabel: data.registrationSubTypeDisplay,
    entityJurisdictionId: data.entityJurisdiction,
    status: data.status,
    canDelete: !!data.canDelete,
    canDeactivate: !!data.canDeactivate,
    isUnmanaged: data.isNotManagedByUs,
  };

  if (rules.hasLocalVatNumber) {
    model.vatNumberLocal = data.numberLocal || notAvailable;
  }

  if (rules.hasFiscaleCode) {
    model.fiscaleCode = data.fiscalCode || notAvailable;
  }

  return model;
};
